import React, { useEffect, useMemo, useState } from 'react';
import { Users, UserPlus, KeyRound, Trash2, Copy, Check, X, RefreshCw, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminScope, ScopeCity } from '../../lib/api';
import {
    DashboardUser,
    DashboardRole,
    DASHBOARD_ROLES,
    ROLE_LABELS,
    ROLE_DESCRIPTIONS,
    listDashboardUsers,
    createDashboardUser,
    updateDashboardUser,
    resetDashboardUserPassword,
    deleteDashboardUser,
    parseApiError,
} from '../../services/adminUsers';

const roleBadgeClass: Record<DashboardRole, string> = {
    admin: 'bg-indigo-100 text-indigo-700',
    viewer: 'bg-gray-100 text-gray-700',
    session_viewer: 'bg-amber-100 text-amber-700',
    insights: 'bg-emerald-100 text-emerald-700',
};

/**
 * Show a grant as the municipality's name, and tell the truth about the two ways
 * one can be wrong — this page is where you'd notice either.
 *
 * Unresolvable ("KBH") means the account sees nothing at all. A legacy alias
 * ("copenhagen") means it sees sessions but is refused that city's settlement
 * reports; re-saving through Edit access stores the canonical id and fixes it.
 */
function cityLabel(cities: ScopeCity[], id: string): string {
    const exact = cities.find(c => c.id === id);
    if (exact) return exact.name;

    const byAlias = cities.find(c => c.aliases.includes(id));
    if (byAlias) return `${byAlias.name} (old id — re-save to fix)`;

    return `${id} (unknown — sees nothing!)`;
}

/**
 * The stored grants as canonical ids, so the picker opens with the right boxes
 * ticked for an account saved under an old id. Without this, editing a legacy
 * "copenhagen" account showed nothing selected and saving would have silently
 * removed its access. Unresolvable grants drop out — there is nothing to tick.
 */
function canonicalSelection(cities: ScopeCity[], grants: string[]): string[] {
    const resolved = grants
        .map(id => cities.find(c => c.id === id || c.aliases.includes(id))?.id)
        .filter((id): id is string => !!id);
    return Array.from(new Set(resolved));
}

function formatDate(iso: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('da-DK', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return iso;
    }
}

/** One-time temp-password reveal. It's only ever returned once by the API. */
const TempPasswordModal: React.FC<{ username: string; tempPassword: string; onClose: () => void }> = ({
    username,
    tempPassword,
    onClose,
}) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(tempPassword);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // clipboard may be unavailable; the value is visible to copy manually
        }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <h3 className="text-lg font-semibold text-gray-900">Temporary password for {username}</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Share this with the user over a secure channel. They'll be asked to change it on first login.
                    <span className="font-medium"> It won't be shown again.</span>
                </p>
                <div className="mt-4 flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 rounded font-mono text-sm break-all">{tempPassword}</code>
                    <button
                        onClick={copy}
                        className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-600"
                        title="Copy"
                    >
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </button>
                </div>
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Municipality picker.
 *
 * This used to be a free-text box, which had no safety net: "kbh" worked,
 * "copenhagen" showed sessions but 403'd the settlement files, and anything else
 * ("KBH", "København") produced an account that logged in fine and saw zero rows
 * with no error anywhere. Picking from the panel's own city list makes all three
 * impossible — and the server rejects unknown ids regardless of what is sent.
 */
const CityPicker: React.FC<{
    cities: ScopeCity[];
    selected: string[];
    onChange: (selected: string[]) => void;
}> = ({ cities, selected, onChange }) => {
    const toggle = (id: string) => {
        onChange(selected.includes(id) ? selected.filter(c => c !== id) : [...selected, id]);
    };

    if (cities.length === 0) {
        return <p className="text-sm text-gray-500">No municipalities available.</p>;
    }

    return (
        <div className="space-y-2 rounded-md border border-gray-200 p-3 max-h-56 overflow-y-auto">
            {cities.map(city => (
                <label key={city.id} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={selected.includes(city.id)}
                        onChange={() => toggle(city.id)}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                        <span className="text-gray-900">{city.name}</span>
                        <span className="ml-2 font-mono text-xs text-gray-400">{city.id}</span>
                        {city.reporting === null && (
                            <span className="block text-xs text-gray-500">
                                Sessions only — no settlement report for this city yet
                            </span>
                        )}
                    </span>
                </label>
            ))}
        </div>
    );
};

/** Change which municipalities an existing viewer account can see. */
const EditAccessModal: React.FC<{
    user: DashboardUser;
    cities: ScopeCity[];
    onClose: () => void;
    onSaved: () => void;
}> = ({ user, cities, onClose, onSaved }) => {
    const [selected, setSelected] = useState<string[]>(
        canonicalSelection(cities, user.allowedCities || [])
    );
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const dropped = (user.allowedCities || []).filter(
        id => !cities.some(c => c.id === id || c.aliases.includes(id))
    );

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSaving(true);
        try {
            await updateDashboardUser(user.id, { allowedCities: selected });
            onSaved();
        } catch (err) {
            setError(parseApiError(err));
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Access for {user.username}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
                )}

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Municipalities</label>
                        <CityPicker cities={cities} selected={selected} onChange={setSelected} />
                        <p className="mt-2 text-xs text-gray-500">
                            Takes effect on their next page load — no need for them to sign out.
                        </p>
                    </div>

                    {dropped.length > 0 && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            This account is set to <strong>{dropped.join(', ')}</strong>, which matches no
                            municipality — it currently sees nothing. Tick the right one and save to fix it.
                        </div>
                    )}

                    {user.allowedAreaNos.length > 0 && (
                        <p className="text-xs text-gray-500">
                            This account also has area grants ({user.allowedAreaNos.join(', ')}), which are
                            set outside this panel and are left untouched.
                        </p>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium"
                        >
                            {isSaving ? 'Saving…' : 'Save access'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AddUserModal: React.FC<{
    cities: ScopeCity[];
    onClose: () => void;
    onCreated: (username: string, tempPassword: string) => void;
}> = ({ cities, onClose, onCreated }) => {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    // Session viewer is what new accounts are actually for (municipality staff).
    // Defaulting to 'viewer' handed out accounts that could log in and then reach
    // nothing — every page requires admin and the sessions API rejects the role.
    const [role, setRole] = useState<DashboardRole>('session_viewer');
    const [allowedCities, setAllowedCities] = useState<string[]>([]);
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (role === 'session_viewer' && allowedCities.length === 0) {
            setError('Pick at least one municipality — a viewer with no municipality sees nothing.');
            return;
        }
        setIsSaving(true);
        try {
            const cities = role === 'session_viewer' ? allowedCities : undefined;
            const { user, tempPassword } = await createDashboardUser({
                username: username.trim(),
                email: email.trim(),
                role,
                allowedCities: cities,
            });
            onCreated(user.username, tempPassword);
        } catch (err) {
            setError(parseApiError(err));
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Add dashboard user</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
                )}

                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                        <input
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value as DashboardRole)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {DASHBOARD_ROLES.map((r) => (
                                <option key={r} value={r}>
                                    {ROLE_LABELS[r]}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">{ROLE_DESCRIPTIONS[role]}</p>
                    </div>
                    {role === 'session_viewer' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Municipalities <span className="text-gray-400">(what this account can see)</span>
                            </label>
                            <CityPicker cities={cities} selected={allowedCities} onChange={setAllowedCities} />
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium"
                        >
                            {isSaving ? 'Creating…' : 'Create user'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AdminUsersPage: React.FC = () => {
    const { user: currentUser } = useAuth();
    // An admin's own scope is every city, which is exactly the set assignable to
    // a viewer account — so the picker reads the same endpoint the panel renders
    // its Reports page from, rather than keeping a second copy of the list.
    const { scope } = useAdminScope();
    const assignableCities = scope?.cities || [];
    const [users, setUsers] = useState<DashboardUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAdd, setShowAdd] = useState(false);
    const [editAccessFor, setEditAccessFor] = useState<DashboardUser | null>(null);
    const [tempReveal, setTempReveal] = useState<{ username: string; tempPassword: string } | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            setError('');
            setUsers(await listDashboardUsers());
        } catch (err) {
            setError(parseApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void load();
    }, []);

    const activeAdminCount = useMemo(
        () => users.filter((u) => u.role === 'admin' && u.isActive).length,
        [users]
    );

    // A change is guarded when it would remove your own admin access or drop the
    // system below one active admin. The backend enforces the same rules; the UI
    // just disables the buttons so it's obvious why.
    const isSelf = (u: DashboardUser) => currentUser?.id === u.id;
    const isLastActiveAdmin = (u: DashboardUser) => u.role === 'admin' && u.isActive && activeAdminCount <= 1;
    const guarded = (u: DashboardUser) => isSelf(u) || isLastActiveAdmin(u);

    const runMutation = async (id: string, fn: () => Promise<unknown>) => {
        setBusyId(id);
        setError('');
        try {
            await fn();
            await load();
        } catch (err) {
            setError(parseApiError(err));
        } finally {
            setBusyId(null);
        }
    };

    const changeRole = (u: DashboardUser, role: DashboardRole) => {
        if (role === u.role) return;
        void runMutation(u.id, () => updateDashboardUser(u.id, { role }));
    };

    const toggleActive = (u: DashboardUser) => {
        if (!u.isActive || window.confirm(`Deactivate ${u.username}? They will no longer be able to log in.`)) {
            void runMutation(u.id, () => updateDashboardUser(u.id, { isActive: !u.isActive }));
        }
    };

    const resetPassword = (u: DashboardUser) => {
        if (!window.confirm(`Reset the password for ${u.username}? Their current password stops working immediately.`)) return;
        void runMutation(u.id, async () => {
            const { tempPassword } = await resetDashboardUserPassword(u.id);
            setTempReveal({ username: u.username, tempPassword });
        });
    };

    const remove = (u: DashboardUser) => {
        if (!window.confirm(`Permanently delete ${u.username}? This cannot be undone.`)) return;
        void runMutation(u.id, () => deleteDashboardUser(u.id));
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                        <Users className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Team</h1>
                        <p className="text-sm text-gray-500">Who can access the admin panel, and at what level.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => void load()}
                        className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                        title="Refresh"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => setShowAdd(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
                    >
                        <UserPlus className="h-4 w-4" />
                        Add user
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
            )}

            <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-500">User</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-500">Role</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-500">Last login</th>
                            <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                                    Loading…
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                                    No users yet.
                                </td>
                            </tr>
                        ) : (
                            users.map((u) => {
                                const rowBusy = busyId === u.id;
                                const lockActions = guarded(u);
                                return (
                                    <tr key={u.id} className={rowBusy ? 'opacity-50' : ''}>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-900">
                                                {u.username}
                                                {isSelf(u) && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                                            </div>
                                            <div className="text-gray-500">{u.email}</div>
                                            {u.role === 'session_viewer' && (
                                                u.allowedCities.length > 0 || u.allowedAreaNos.length > 0 ? (
                                                    <div className="text-xs text-amber-700 mt-0.5">
                                                        {[
                                                            ...u.allowedCities.map(id => cityLabel(assignableCities, id)),
                                                            ...(u.allowedAreaNos.length > 0 ? [`areas ${u.allowedAreaNos.join(', ')}`] : [])
                                                        ].join(' · ')}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-red-600 mt-0.5">
                                                        No municipality assigned — this account sees nothing
                                                    </div>
                                                )
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={u.role}
                                                disabled={rowBusy || lockActions}
                                                onChange={(e) => changeRole(u, e.target.value as DashboardRole)}
                                                className={`text-xs font-medium rounded px-2 py-1 border-0 focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed ${roleBadgeClass[u.role]}`}
                                                title={lockActions ? 'Guarded: this is you or the last active admin' : ROLE_DESCRIPTIONS[u.role]}
                                            >
                                                {DASHBOARD_ROLES.map((r) => (
                                                    <option key={r} value={r}>
                                                        {ROLE_LABELS[r]}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.isActive ? (
                                                <span className="inline-flex items-center gap-1 text-green-700">
                                                    <span className="h-2 w-2 rounded-full bg-green-500" /> Active
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-gray-400">
                                                    <span className="h-2 w-2 rounded-full bg-gray-300" /> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-500">{formatDate(u.lastLogin)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                {u.role === 'session_viewer' && (
                                                    <button
                                                        onClick={() => setEditAccessFor(u)}
                                                        disabled={rowBusy}
                                                        className="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                                                        title="Edit municipality access"
                                                    >
                                                        <Building2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => resetPassword(u)}
                                                    disabled={rowBusy}
                                                    className="p-2 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                                                    title="Reset password"
                                                >
                                                    <KeyRound className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => toggleActive(u)}
                                                    disabled={rowBusy || lockActions}
                                                    className="px-2 py-1 rounded-md text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    title={lockActions ? 'Guarded: this is you or the last active admin' : u.isActive ? 'Deactivate' : 'Activate'}
                                                >
                                                    {u.isActive ? 'Deactivate' : 'Activate'}
                                                </button>
                                                <button
                                                    onClick={() => remove(u)}
                                                    disabled={rowBusy || lockActions}
                                                    className="p-2 rounded-md text-red-500 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    title={lockActions ? 'Guarded: this is you or the last active admin' : 'Delete'}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <p className="mt-3 text-xs text-gray-400">
                You can't remove your own admin access or the last active admin — that guard is enforced on the server too.
                Locked out entirely? Recover from the server with <code>node scripts/setup/reset-admin.js</code>.
            </p>

            {editAccessFor && (
                <EditAccessModal
                    user={editAccessFor}
                    cities={assignableCities}
                    onClose={() => setEditAccessFor(null)}
                    onSaved={() => {
                        setEditAccessFor(null);
                        void load();
                    }}
                />
            )}
            {showAdd && (
                <AddUserModal
                    cities={assignableCities}
                    onClose={() => setShowAdd(false)}
                    onCreated={(username, tempPassword) => {
                        setShowAdd(false);
                        setTempReveal({ username, tempPassword });
                        void load();
                    }}
                />
            )}
            {tempReveal && (
                <TempPasswordModal
                    username={tempReveal.username}
                    tempPassword={tempReveal.tempPassword}
                    onClose={() => setTempReveal(null)}
                />
            )}
        </div>
    );
};

export default AdminUsersPage;
